import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as CompanyIcon,
  Person as AnalystIcon,
  Description as DocIcon,
  Dashboard as PageIcon,
} from '@mui/icons-material';
import { getCompanies, getAnalysts, CompanyListItem, AnalystListItem } from '../services/v3RagService';

interface CommandItem {
  type: 'company' | 'analyst' | 'page';
  label: string;
  secondary?: string;
  path: string;
  icon: React.ReactNode;
}

const PAGES: CommandItem[] = [
  { type: 'page', label: 'Agent Chat', path: '/agent', icon: <PageIcon /> },
  { type: 'page', label: 'Documents', path: '/documents', icon: <DocIcon /> },
  { type: 'page', label: 'Companies', path: '/companies', icon: <CompanyIcon /> },
  { type: 'page', label: 'Analysts', path: '/analysts', icon: <AnalystIcon /> },
  { type: 'page', label: 'PDF AI Chat', path: '/pdf-ai-chat', icon: <DocIcon /> },
  { type: 'page', label: 'Replay Center', path: '/replay-center', icon: <PageIcon /> },
  { type: 'page', label: 'Portal Monitor', path: '/portal-monitor', icon: <PageIcon /> },
  { type: 'page', label: 'Data Quality', path: '/data-quality', icon: <PageIcon /> },
];

const CommandBar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [analysts, setAnalysts] = useState<AnalystListItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const dataLoaded = useRef(false);

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Load data on first open
  useEffect(() => {
    if (open && !dataLoaded.current) {
      dataLoaded.current = true;
      getCompanies().then(setCompanies).catch(() => {});
      getAnalysts().then(setAnalysts).catch(() => {});
    }
  }, [open]);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const allItems = useMemo((): CommandItem[] => {
    const companyItems: CommandItem[] = companies.map(c => ({
      type: 'company' as const,
      label: `${c.ticker} - ${c.name || c.ticker}`,
      secondary: c.sector || undefined,
      path: `/company/${c.ticker}`,
      icon: <CompanyIcon />,
    }));

    const analystItems: CommandItem[] = analysts.map(a => ({
      type: 'analyst' as const,
      label: a.name,
      secondary: a.sector_focus || a.firm || undefined,
      path: `/analyst/${a.id}`,
      icon: <AnalystIcon />,
    }));

    return [...PAGES, ...companyItems, ...analystItems];
  }, [companies, analysts]);

  const filtered = useMemo(() => {
    if (!query.trim()) return PAGES; // Show pages by default
    const q = query.toLowerCase();
    return allItems
      .filter(item =>
        item.label.toLowerCase().includes(q) ||
        (item.secondary && item.secondary.toLowerCase().includes(q))
      )
      .slice(0, 15);
  }, [query, allItems]);

  const handleSelect = useCallback((item: CommandItem) => {
    setOpen(false);
    navigate(item.path);
  }, [navigate]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      e.preventDefault();
      handleSelect(filtered[selectedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, [filtered, selectedIndex, handleSelect]);

  // Reset selection on query change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const typeColor = (type: string) => {
    switch (type) {
      case 'company': return 'primary';
      case 'analyst': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '15%',
          m: 0,
          borderRadius: 2,
          maxHeight: '60vh',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Search companies, analysts, pages..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary" sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  px: 0.5,
                  fontSize: '0.65rem',
                }}>
                  ESC
                </Typography>
              </InputAdornment>
            ),
            sx: { fontSize: '1rem' },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
            },
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        />
        <List sx={{ maxHeight: 'calc(60vh - 56px)', overflow: 'auto', py: 0.5 }}>
          {filtered.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No results found</Typography>
            </Box>
          ) : (
            filtered.map((item, i) => (
              <ListItemButton
                key={`${item.type}-${item.path}`}
                selected={i === selectedIndex}
                onClick={() => handleSelect(item)}
                sx={{
                  py: 1,
                  px: 2,
                  '&.Mui-selected': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={item.secondary}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <Chip
                  label={item.type}
                  size="small"
                  color={typeColor(item.type) as any}
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              </ListItemButton>
            ))
          )}
        </List>
      </Box>
    </Dialog>
  );
};

export default CommandBar;
